import React, {
    ChangeEvent,
    InputHTMLAttributes,
    KeyboardEvent,
    useCallback,
    useRef,
    useState,
} from "react";
// Import the new CSS file
import "./checkbox.module.css"; 

const SquareIcon = (props: React.SVGProps<SVGSVGElement>) => (
   <svg
    fill="#CDCDCD"
    style={{
        width: "20px",
        height: "20px",
        border: "1px solid #CDCDCD",
        borderRadius: "6px",
        background: "#fff",
    }}
    xmlns="http://www.w3.org/2000/svg"
></svg>
);

// 2. The default checked icon
const CheckSquareIcon = (props: React.SVGProps<SVGSVGElement>) => (
   <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
>
    <rect
        x="0"
        y="0"
        width="20"
        height="20"
        rx="6"
        fill="#3B97B1"
    />
    <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M6.25 9.25L8.75 11.75L13.75 6.75L15.25 8.25L8.75 14.75L5.25 11.25L6.25 10.25L6.25 9.25Z"
        fill="white"
    />
</svg>
);

// 3. The new "pricingRedesign" checked icon (using the SVG you provided)
const CheckSquareIconNew = (props: React.SVGProps<SVGSVGElement>) => (
<svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg">
    <rect
        x="0"
        y="0"
        width="20"
        height="20"
        rx="6"
        fill="#3B97B1"
    />
    <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M6.25 9.25L8.75 11.75L13.75 6.75L15.25 8.25L8.75 14.75L5.25 11.25L6.25 10.25L6.25 9.25Z"
        fill="white"
    />
</svg>
);

// UI props are the same
export interface CheckboxUIProps {
    /**
     * Color to use when checked, defaults to primaryText
     * (Note: This prop was not used in the original styled-component)
     */
    uiColor?: any;
    /**
     * Disabled checkbox
     */
    uiDisabled?: boolean;
    /**
     * Icon for non checked checkbox
     */
    uiIcon?: React.ReactElement<any>;
    /**
     * Icon for checked checkbox
     */
    uiCheckIcon?: React.ReactElement<any>;
    /**
     * Icon for checked pricingRedesign
     */
    pricingRedesign?: boolean;
    /**
     * Validation error flag
     */
    uiError?: boolean;
}

// Full props
export type CheckboxProps = InputHTMLAttributes<HTMLInputElement> & CheckboxUIProps;

/**
 * Just an ordinary checkbox, refactored to use Hooks and plain CSS.
 */
const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(
    (props, ref) => {
        const {
            // UI Props
            uiColor, // (unused in original styling)
            uiDisabled,
            uiIcon,
            uiCheckIcon,
            pricingRedesign,
            uiError,

            // Standard Props
            className,
            children,
            tabIndex,

            // Controlled/Uncontrolled Props
            checked,
            defaultChecked,
            onChange,

            ...other // Rest of the props for the <input>
        } = props;

        // Internal state for uncontrolled component
        const [internalChecked, setInternalChecked] = useState(
            defaultChecked || false
        );

        // Determine if controlled or uncontrolled
        const isControlled = checked != null;
        const displayChecked = isControlled ? checked : internalChecked;

        // Ref for the root <label> element
        const rootRef = useRef<HTMLLabelElement>(null);

        // --- Event Handlers ---

        const handleChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
            // Call parent onChange if it exists
            if (onChange) {
                onChange(e);
            }

            // If uncontrolled, update internal state
            if (!isControlled) {
                setInternalChecked(!internalChecked);
            }
        }, [onChange, isControlled, internalChecked]);

        const onKeyDown = useCallback((e: KeyboardEvent<HTMLElement>) => {
            // space key
            if (e.keyCode !== 32 || !rootRef.current) {
                return;
            }

            // Find the <input> element
            const input = rootRef.current.querySelector<HTMLInputElement>(
                "input[type='checkbox']"
            );

            if (!input) {
                return;
            }

            // Fake the event target, as the original component did
            // to re-use the handleChange logic
            handleChange({
                ...e,
                currentTarget: input,
                target: input,
            } as any); // Using 'any' to bridge the event type
            
        }, [handleChange]);

        // --- Class Names ---
        const classNames = [
            "checkbox-wrapper", // Base class
            uiDisabled ? "checkbox-disabled" : "", // Disabled class
            uiError ? "uiError" : "",
            className, // Pass through parent classes (e.g., 'uiError')
        ]
            .filter(Boolean)
            .join(" ");

        // --- Render ---
        return (
            <label
                className={classNames}
                onKeyDown={onKeyDown}
                tabIndex={tabIndex}
                ref={rootRef}
            >
                {/* Baseline helper from original */}
                <span className="baseline-helper" />

                <input
                    {...other}
                    tabIndex={tabIndex}
                    ref={ref} // Forward the ref to the <input>
                    type="checkbox"
                    checked={displayChecked}
                    disabled={uiDisabled} // Use native disabled
                    className="input"
                    onChange={handleChange}
                />

                {/* Icon logic from original */}
                {displayChecked
                    ? uiCheckIcon ||
                      (pricingRedesign ? <CheckSquareIconNew /> : <CheckSquareIcon />)
                    : uiIcon || <SquareIcon />}

                {/* Label content */}
                {<span className="label">{children}</span>}
            </label>
        );
    }
);

Checkbox.displayName = "Checkbox";

// Set default props (for the unused uiColor)
Checkbox.defaultProps = {
    uiColor: "primary",
};

export default Checkbox;
